import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Facebook, User, MessageCircle, ExternalLink,
  Heart, Users, Sparkles, Trophy, Flame, BarChart3,
  Loader2, Crown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PackChallenge from '../components/PackChallenge';
import PackRewards from '../components/PackRewards';
import PrecisionLeaderboard from '../components/PrecisionLeaderboard';

// ============ WALL OF FAME ============
function WallOfFame({ language }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/evolution/wall-of-fame')
      .then(res => setEntries(res.data.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'personal_record': return <Trophy className="w-4 h-4" />;
      case 'milestone': return <Flame className="w-4 h-4" />;
      case 'consistency_7d': return <BarChart3 className="w-4 h-4" />;
      default: return <Trophy className="w-4 h-4" />;
    }
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now - d;
    const diffH = Math.floor(diffMs / (1000 * 60 * 60));
    const diffD = Math.floor(diffH / 24);
    if (diffD > 0) return language === 'fr' ? `il y a ${diffD}j` : `${diffD}d ago`;
    if (diffH > 0) return language === 'fr' ? `il y a ${diffH}h` : `${diffH}h ago`;
    return language === 'fr' ? 'maintenant' : 'now';
  };

  if (loading) {
    return (
      <Card className="bg-card/50 border-border p-6">
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (entries.length === 0) return null;

  return (
    <Card className="bg-card/50 border-border overflow-hidden" data-testid="wall-of-fame">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 flex items-center justify-center">
          <Crown className="w-4 h-4 text-amber-400" />
        </div>
        <div>
          <h3 className="font-heading text-base uppercase tracking-wider text-foreground">
            Wall of Fame
          </h3>
          <p className="text-[11px] text-muted-foreground">
            {language === 'fr' ? 'Les exploits recents de la communaute' : 'Recent community achievements'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider">Live</span>
        </div>
      </div>

      {/* Feed */}
      <div className="px-3 pb-3 space-y-1.5 max-h-[320px] overflow-y-auto">
        <AnimatePresence>
          {entries.map((entry, i) => (
            <motion.div
              key={`${entry.type}-${entry.created_at}-${i}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06, duration: 0.3 }}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors group"
              data-testid={`wall-entry-${i}`}
            >
              {/* Icon */}
              <div
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5"
                style={{ backgroundColor: `${entry.color}18`, border: `1.5px solid ${entry.color}40` }}
              >
                <span style={{ color: entry.color }}>{getIcon(entry.type)}</span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground leading-snug">
                  <span className="font-bold" style={{ color: entry.color }}>{entry.first_name}</span>
                  {' '}
                  {entry.type === 'personal_record'
                    ? (language === 'fr' ? 'a atteint un nouveau record personnel !' : 'hit a new personal record!')
                    : entry.type === 'milestone'
                    ? (language === 'fr' ? `a perdu ${entry.text.match(/\d+/)?.[0] || '?'} lbs depuis le debut !` : `lost ${entry.text.match(/\d+/)?.[0] || '?'} lbs since starting!`)
                    : (language === 'fr' ? 'a logue son poids 7 jours de suite !' : 'logged weight 7 days in a row!')
                  }
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {timeAgo(entry.created_at)}
                </p>
              </div>

              {/* Reaction hint */}
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-lg cursor-pointer hover:scale-125 transition-transform inline-block">
                  {entry.type === 'personal_record' ? '🏆' : entry.type === 'milestone' ? '🔥' : '📈'}
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-border/50 bg-muted/10">
        <p className="text-[11px] text-center text-muted-foreground">
          {language === 'fr'
            ? `${entries.length} exploit${entries.length > 1 ? 's' : ''} ces 30 derniers jours`
            : `${entries.length} achievement${entries.length > 1 ? 's' : ''} in the last 30 days`
          }
        </p>
      </div>
    </Card>
  );
}

// ============ MAIN COMMUNITY PAGE ============
const Community = () => {
  const { language } = useLanguage();

  const communityLinks = [
    {
      id: 'facebook-group',
      icon: Users,
      title: language === 'fr' ? 'Groupe Facebook Prive' : 'Private Facebook Group',
      description: language === 'fr' 
        ? 'Notre quartier general! Partage tes repas, tes victoires et motive la gang. C\'est ici que la magie opere.'
        : 'Our HQ! Share your meals, victories and motivate the gang. This is where the magic happens.',
      url: 'https://www.facebook.com/groups/753188122904936/?ref=share&mibextid=NSMWBT',
      color: 'bg-blue-600 hover:bg-blue-700',
      buttonText: language === 'fr' ? 'Rejoindre le groupe' : 'Join Group'
    },
    {
      id: 'facebook',
      icon: Facebook,
      title: language === 'fr' ? 'Page Facebook Extra Miles' : 'Extra Miles Facebook Page',
      description: language === 'fr' 
        ? 'Suivez nos dernieres actualites, conseils nutrition et succes de la communaute!'
        : 'Follow our latest news, nutrition tips and community success stories!',
      url: 'https://www.facebook.com/profile.php?id=61575227498498',
      color: 'bg-blue-500 hover:bg-blue-600',
      buttonText: language === 'fr' ? 'Suivre la page' : 'Follow Page'
    },
    {
      id: 'coach',
      icon: User,
      title: language === 'fr' ? 'Coach Yuri Octeau' : 'Coach Yuri Octeau',
      description: language === 'fr'
        ? 'Le Chef derriere la machine. Connectez-vous directement avec Coach Yuri.'
        : 'The Chef behind the machine. Connect directly with Coach Yuri.',
      url: 'https://www.facebook.com/yuri.octeau',
      color: 'bg-orange-500 hover:bg-orange-600',
      buttonText: language === 'fr' ? 'Voir le profil' : 'View Profile'
    },
    {
      id: 'messenger',
      icon: MessageCircle,
      title: language === 'fr' ? 'Support Messenger' : 'Messenger Support',
      description: language === 'fr'
        ? 'Besoin d\'aide? Ecris-nous directement sur Messenger. On est la pour toi.'
        : 'Need help? Write to us directly on Messenger. We\'re here for you.',
      url: 'https://m.me/j/AbabLe_jbpjfCyxI/',
      color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
      buttonText: language === 'fr' ? 'Ecrire un message' : 'Send Message'
    },
    {
      id: 'website',
      icon: ExternalLink,
      title: 'extramiles.ca',
      description: language === 'fr'
        ? 'Decouvrez tous nos services: coaching prive, plans d\'entraînement, et plus encore.'
        : 'Discover all our services: private coaching, training plans, and more.',
      url: 'https://www.extramiles.ca',
      color: 'bg-emerald-600 hover:bg-emerald-700',
      buttonText: language === 'fr' ? 'Visiter le site' : 'Visit Website'
    }
  ];

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-2">
          <Heart className="w-8 h-8 text-primary fill-primary" />
          <h1 className="font-heading text-3xl uppercase tracking-wider">
            {language === 'fr' ? 'Communaute' : 'Community'}
          </h1>
          <Heart className="w-8 h-8 text-primary fill-primary" />
        </div>
        <p className="text-muted-foreground max-w-md mx-auto">
          {language === 'fr' 
            ? 'Rejoins la famille YUMIX! Ensemble, on va plus loin.' 
            : 'Join the YUMIX family! Together, we go further.'}
        </p>
      </div>

      {/* WALL OF FAME */}
      <WallOfFame language={language} />

      {/* TOP PRÉCISION (FatSecret) — gamifies the trust metric */}
      <PrecisionLeaderboard />

      {/* DEFI DE LA MEUTE */}
      <PackChallenge />

      {/* RECOMPENSES DE MEUTE */}
      <PackRewards />

      {/* Stats Banner */}
      <Card className="bg-gradient-to-r from-primary/20 to-accent/20 border-primary/30 p-4">
        <div className="flex items-center justify-center gap-6 text-center">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <span className="font-heading text-xl text-primary">ALL IN</span>
          </div>
          <div className="h-6 w-px bg-border" />
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            <span className="font-heading text-xl text-accent">ALL TIME</span>
          </div>
        </div>
      </Card>

      {/* Community Links */}
      <div className="space-y-4">
        {communityLinks.map((link) => {
          const Icon = link.icon;
          return (
            <Card 
              key={link.id}
              className="bg-card/50 border-border p-5 hover:border-primary/50 transition-colors"
              data-testid={`community-link-${link.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${link.color} text-white flex-shrink-0`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="font-heading text-lg uppercase tracking-wide">
                    {link.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {link.description}
                  </p>
                  <Button
                    onClick={() => window.open(link.url, '_blank')}
                    className={`${link.color} text-white mt-2`}
                    data-testid={`community-btn-${link.id}`}
                  >
                    {link.buttonText}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Motivational Footer */}
      <Card className="bg-secondary/50 border-border p-6 text-center">
        <p className="font-heading text-xl uppercase tracking-wide text-primary mb-2">
          {language === 'fr' ? '"On lache pas!"' : '"Never give up!"'}
        </p>
        <p className="text-sm text-muted-foreground">
          {language === 'fr' 
            ? "- L'équipe YUMIX" 
            : '- The YUMIX team'}
        </p>
      </Card>
    </div>
  );
};

export default Community;
